import { readRemedy, type AccessRemedy, type Decision, type RefusalConcern } from "../../../api/access/decisions";
import type { ThreadViewer, ViewerAction, ViewerActionName } from "../../../api/threads/liveMessages";

/**
 * What the viewer may do in the Run, as module C decides it for them (W-22 slice 2): the same rules its commands
 * apply, so the web offers exactly what the server accepts. `target` names the turn, request or step an action is
 * for. An action the viewer section doesn't list isn't offered. A permission the viewer lacks is module A's refusal,
 * with A's remedy (SOT-02, GS-8).
 */
export type ViewerRefusal = NonNullable<ViewerAction["refusal"]>;

export interface ActionDecision {
  readonly allowed: boolean;
  /** Why not, when the server says; `null` when allowed or not listed. */
  readonly refusal: ViewerRefusal | null;
  readonly concern: RefusalConcern | null;
  /** What would open it, for a permission the viewer lacks. */
  readonly remedy: AccessRemedy | null;
}

const NOT_LISTED: ActionDecision = { allowed: false, refusal: null, concern: null, remedy: null };

function listed(viewer: ThreadViewer, action: ViewerActionName, target: string | null): ViewerAction | undefined {
  return (viewer.actions ?? []).find((candidate) => candidate.action === action && (target === null || candidate.target === target));
}

export function actionDecision(viewer: ThreadViewer, action: ViewerActionName, target: string | null = null): ActionDecision {
  const found = listed(viewer, action, target);
  if (found === undefined) return NOT_LISTED;
  const refusal = found.refusal ?? null;
  return { allowed: refusal === null, refusal, concern: found.concern ?? null, remedy: readRemedy(found.remedy) };
}

export function allows(viewer: ThreadViewer, action: ViewerActionName, target: string | null = null): boolean {
  return actionDecision(viewer, action, target).allowed;
}

/** The decision in the shape every module's refusal is shown in, asked for in the Run's project. */
export function asDecision(decision: ActionDecision, projectId: string): Decision {
  return { refusal: decision.refusal, concern: decision.concern, remedy: decision.remedy, projectId };
}

/** An action the screen shows: with the reason it is refused to this viewer, if it is. */
export interface ActionOffer {
  readonly refusal: ViewerRefusal | null;
}

/** The action as the screen offers it; `null` when it isn't shown. Module C says what a refusal is about (P-B): one
 * about who the viewer is, or about what must happen first, keeps the action in view with its reason; one about where
 * the Run is now leaves it out. */
export function actionOffer(viewer: ThreadViewer, action: ViewerActionName, target: string | null = null): ActionOffer | null {
  const found = listed(viewer, action, target);
  if (found === undefined) return null;
  const refusal = found.refusal ?? null;
  if (refusal === null) return { refusal: null };
  return found.concern === "state" ? null : { refusal };
}
